/**
 * Currency conversion on the account (D-070, D-071; old `handleConfirmCurrencyConversion` App.tsx:21081-21359) as
 * `account_conversion`: the account's own USD and IQD lines (same person, same kind). Trust USD→IQD / debt IQD→USD =
 * the office buys USD (rate pre-filled with the approved buy quote); trust IQD→USD / debt USD→IQD = the office sells
 * USD (approved sell quote, plus the cost pre-filled with the office-wide weighted average, D-239 — changing it or a
 * rate below it needs the PIN, D-065 / D-018). The amount is the USD amount in both directions (no client division,
 * D-012); the IQD figure, both new balances and the profit come from the server's dry run before the PIN (D-071).
 */
import { useEffect, useRef, useState } from "react";
import { CustomerConvertModal, type ConversionDirection } from "@faylasuf/ui";
import type { CustomerAccountRow, CustomersSummary, PreviewResult } from "../../api/types.js";
import { useApp } from "../../app/context.js";
import { errorMessage } from "../../app/errors.js";
import { useGateway } from "../../app/operations.js";
import { useToast } from "../../app/toast.js";
import { decCmp, fmt, kindLabel, positive } from "./format.js";

export function ConvertContainer({ row, quotes, onClose }: { row: CustomerAccountRow; quotes: CustomersSummary["usdQuotes"]; onClose: () => void }) {
  const { t } = useApp();
  const toast = useToast();
  const gateway = useGateway();
  const [direction, setDirection] = useState<ConversionDirection>("USD_to_IQD");
  const sellTypeOf = (d: ConversionDirection) => (row.kind === "trust" ? d === "IQD_to_USD" : d === "USD_to_IQD");
  const sellType = sellTypeOf(direction);
  const quoteFor = (d: ConversionDirection) => (quotes ? (sellTypeOf(d) ? quotes.sell : quotes.buy) : "");
  const [amount, setAmount] = useState("");
  const [rate, setRate] = useState(quoteFor("USD_to_IQD"));
  const [cost, setCost] = useState(quotes?.averageCost ?? "");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  const from = direction === "USD_to_IQD" ? row.usd.accountId : row.iqd.accountId;
  const to = direction === "USD_to_IQD" ? row.iqd.accountId : row.usd.accountId;
  const input = positive(amount) && positive(rate) && (!sellType || positive(cost))
    ? { fromAccountId: from, toAccountId: to, usd: amount, quote: rate, ...(sellType ? { costQuote: cost } : {}), ...(note.trim() ? { note: note.trim() } : {}) }
    : null;

  useEffect(() => {
    setPreview(null);
    setRefusal(null);
    if (!input) return;
    const my = ++seq.current;
    const h = setTimeout(() => {
      gateway.preview("account_conversion", input).then(
        (pv) => {
          if (my === seq.current) setPreview(pv);
        },
        (e: unknown) => {
          if (my === seq.current) setRefusal(errorMessage(t, e));
        },
      );
    }, 350);
    return () => clearTimeout(h);
  }, [from, to, amount, rate, cost, sellType, note]);

  const pinMessage = (): string => {
    if (sellType && quotes && positive(cost) && decCmp(cost, quotes.averageCost) !== 0) return t("cust.pin_convert_cost", { prefilled: quotes.averageCost, cost });
    if (sellType && positive(cost) && decCmp(rate, cost) < 0) return t("cust.pin_convert_loss");
    return t("cust.pin_convert", { name: row.name });
  };

  const confirm = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const res = await gateway.commitPreview(preview, { pinMessage: pinMessage() });
      if (res) onClose();
    } catch {
      /* toasted by the gateway */
    } finally {
      setBusy(false);
    }
  };

  const d = preview?.derived;
  return (
    <>
      <CustomerConvertModal
        name={row.name}
        kindLabel={kindLabel(t, row.kind)}
        usdAvailableLabel={fmt(row.usd.balance)}
        iqdAvailableLabel={fmt(row.iqd.balance)}
        direction={direction}
        onDirectionChange={(dir) => {
          setDirection(dir);
          setAmount("");
          setRate(quoteFor(dir));
          if (quotes) setCost(quotes.averageCost);
          if (!quotes) toast(t("cust.convert_no_quotes"), "info");
        }}
        amount={amount}
        onAmountChange={setAmount}
        rate={rate}
        onRateChange={setRate}
        cost={sellType ? cost : null}
        onCostChange={setCost}
        preview={d ? { iqdLabel: fmt(d["iqd"] ?? "0"), usdAfterLabel: fmt(d["usd_balance_after"] ?? "0"), iqdAfterLabel: fmt(d["iqd_balance_after"] ?? "0"), profitLabel: sellType ? fmt(d["profit_iqd"] ?? "0") : null } : null}
        refusal={refusal}
        note={note}
        onNoteChange={setNote}
        busy={busy}
        canConfirm={preview !== null}
        onConfirm={() => void confirm()}
        onClose={onClose}
      />
      {gateway.pin.dialog}
    </>
  );
}
